import React, { useState } from "react";
import axios from "axios";
import { useApp, API } from "../store";
import { Cpu, Compass, Zap, Sparkles } from "lucide-react";

export default function UltraAI() {
  const { t } = useApp();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/ultra-ai-signup`, { email, name, country });
    setOk(true); setEmail(""); setName(""); setCountry("");
  };

  return (
    <main className="pt-16 bg-white" data-testid="ultra-page">
      {/* Hero */}
      <section className="relative h-[88vh] overflow-hidden">
        <img src="https://staging.sharkxelectric.com/api/uploads/9090e3512cab4ff2bef8a045622046e0.webp" alt="SharkX Ultra AI" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/40 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 w-full">
            <div className="max-w-3xl text-white">
              <div className="overline mb-6 text-sky-300">{t.ultra.overline}</div>
              <h1 className="font-serif-display text-7xl md:text-9xl lg:text-[12rem] leading-[0.85]">SharkX<br /><span className="text-sky-400">Ultra AI</span></h1>
              <p className="text-xl md:text-3xl mt-10 max-w-2xl leading-relaxed font-serif-display italic">{t.ultra.body}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <FeatureBig icon={<Cpu size={24} />} k={t.ultraPage.aiFeature} v={t.ultraPage.autonomous} />
          <FeatureBig icon={<Zap size={24} />} k={t.ultraPage.speedFeature} v={t.ultraPage.speedVal} />
          <FeatureBig icon={<Compass size={24} />} k={t.ultraPage.rangeFeature} v={t.ultraPage.rangeVal} />
          <FeatureBig icon={<Sparkles size={24} />} k={t.ultraPage.foilsFeature} v={t.ultraPage.foilsVal} />
        </div>
      </section>

      {/* Story */}
      <section className="py-16 px-6 md:px-12 max-w-4xl mx-auto">
              <div className="overline mb-3">{t.ultraPage.storyOverline}</div>
              <h2 className="font-serif-display text-5xl md:text-7xl text-slate-900">{t.ultraPage.storyTitle}</h2>
              <p className="text-slate-700 mt-8 text-lg leading-relaxed">
                {t.ultraPage.storyBody}
              </p>
              <ul className="mt-10 space-y-4 text-slate-700">
                <li className="flex gap-3"><span className="text-sky-500">→</span><span><strong>{t.ultraPage.navTitle}</strong> {t.ultraPage.navBody}</span></li>
                <li className="flex gap-3"><span className="text-sky-500">→</span><span><strong>{t.ultraPage.stabTitle}</strong> {t.ultraPage.stabBody}</span></li>
                <li className="flex gap-3"><span className="text-sky-500">→</span><span><strong>{t.ultraPage.designTitle}</strong> {t.ultraPage.designBody}</span></li>
              </ul>
      </section>

      {/* Waitlist */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-2xl mx-auto bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-200 rounded-2xl p-10 md:p-14">
          <div className="overline mb-3">{t.ultraPage.waitlistOverline}</div>
          <h3 className="font-serif-display text-4xl md:text-5xl text-slate-900">{t.ultra.cta}</h3>
          <p className="text-slate-700 mt-3">{t.ultraPage.waitlistBody}</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="ultra-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required value={name} onChange={(e) => setName(e.target.value)} placeholder={t.book.name} className="bg-white border border-slate-300 px-5 py-4 rounded-md focus:border-sky-500 focus:outline-none" data-testid="ultra-name" />
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.ultraPage.emailPlaceholder} className="bg-white border border-slate-300 px-5 py-4 rounded-md focus:border-sky-500 focus:outline-none" data-testid="ultra-email" />
              <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder={t.ultraPage.countryPlaceholder} className="md:col-span-2 bg-white border border-slate-300 px-5 py-4 rounded-md focus:border-sky-500 focus:outline-none" data-testid="ultra-country" />
            </div>
            <button type="submit" className="btn-primary" data-testid="ultra-submit">{t.ultra.cta}</button>
            {ok && <div className="text-sky-600 font-ui text-sm uppercase tracking-widest" data-testid="ultra-ok">✓ {t.ultra.ok}</div>}
          </form>
        </div>
      </section>
    </main>
  );
}

function FeatureBig({ icon, k, v }) {
  return (
    <div className="border border-slate-200 bg-white p-7 rounded-md">
      <div className="text-sky-500 mb-3">{icon}</div>
      <div className="overline">{k}</div>
      <div className="font-serif-display text-3xl text-slate-900 mt-1">{v}</div>
    </div>
  );
}
